// コメント機能のオーバーレイ層（ピン・入力ウィンドウ・スレッドポップオーバーの統括）。
//
// 通常オブジェクト層（ステージの transform 内）とは独立した、ビューポート固定サイズの
// fixed オーバーレイとして描画する。各 comment-thread の x/y（アンカー）を screen 座標へ射影し、
// ズーム倍率に依らない一定サイズのピンを表示する（FigJam のコメントピンに合わせる）。
//
// コンテナは pointer-events:none とし、ピン・ポップオーバー・入力ウィンドウだけが
// pointer-events:auto を持つ。これにより、ピン以外の領域のドラッグはキャンバスのパン等へ透過する。

import { useEffect, useMemo, useRef, useState } from "react";
import { isCommentThread } from "@refinement-canvas/domain";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";
import { useCommentAnchorPreviewStore } from "../store/comment-anchor-preview-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { applyWheelZoom, stageToScreen } from "../../../lib/canvas-coordinates";
import { CommentPin } from "./comment-pin";
import { CommentThreadPopover } from "./comment-thread-popover";
import { CommentInputWindow } from "./comment-input-window";
import type { CommentImageUploader } from "./comment-image-upload";

interface Props {
  readonly currentUserId: string;
  readonly currentUserName: string;
  readonly currentUserAvatar?: string | undefined;
  readonly canAdmin: boolean;
  /** 参照専用（埋め込み）。true でスレッド閲覧のみ許可し、投稿 / 編集 / 削除 / 移動を無効化する。 */
  readonly readOnly?: boolean | undefined;
  /** コメントへの画像貼り付けを有効にする場合のアップローダ（参照専用では未指定）。 */
  readonly commentImageUploader?: CommentImageUploader | undefined;
}

export function CommentLayer({
  currentUserId,
  currentUserName,
  currentUserAvatar,
  canAdmin,
  readOnly = false,
  commentImageUploader,
}: Props): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const setView = useCanvasStore((s) => s.setView);
  const objects = useCanvasDocStore((s) => s.objects);
  const anchorPreviews = useCommentAnchorPreviewStore((s) => s.anchors);
  const commentDraft = useCanvasStore((s) => s.commentDraft);
  const setCommentDraft = useCanvasStore((s) => s.setCommentDraft);
  const dispatcher = useCanvasOpDispatcherContext();
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  // 新規コメント / 返信の送信確定処理（添付画像の昇格）中かどうか。送信中は外側クリック・Escape での
  // クローズを抑止し、送信の中断・確定済み URL キャッシュの喪失・公開添付の孤児化を防ぐ
  // （composer → 各サーフェスの onSubmittingChange で伝播）。
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  const [threadSubmitting, setThreadSubmitting] = useState(false);
  // クローズ判定（document リスナのクロージャ内）で最新の送信中フラグを参照するための ref。
  // state を effect 依存に足すとリスナを張り直すため、ref で最新値だけを読む。
  const draftSubmittingRef = useRef(false);
  const threadSubmittingRef = useRef(false);
  draftSubmittingRef.current = draftSubmitting;
  threadSubmittingRef.current = threadSubmitting;
  // ドラッグ移動中のピンの screen オフセット（{ id, dx, dy }）。ドラッグしていないときは null。
  // 開いているポップオーバーを、ドラッグ中のピンと同じだけ追従させるために使う
  // （ピンだけ動いて本文が取り残される／ドラッグでポップオーバーを閉じて未送信テキストを失う、の両方を回避）。
  const [pinDragOffset, setPinDragOffset] = useState<{ id: string; dx: number; dy: number } | null>(
    null
  );
  // Ctrl/Cmd + ホイールのズームを張る対象のオーバーレイ層（ピン層・操作中サーフェス層）。
  const pinLayerRef = useRef<HTMLDivElement>(null);
  const surfaceLayerRef = useRef<HTMLDivElement>(null);

  const threads = useMemo(() => Object.values(objects).filter(isCommentThread), [objects]);

  const openThread = openThreadId ? objects[openThreadId] : undefined;
  const openCommentThread = openThread && isCommentThread(openThread) ? openThread : undefined;
  // 操作中サーフェス層（ポップオーバー / 入力ウィンドウ）が表示中か。表示の有無が変わるたびに
  // ホイールリスナーを張り直すための依存に使う（boolean なので不要な再実行を起こさない）。
  const surfaceShown = Boolean(openCommentThread || commentDraft);

  // 開いていたスレッドが削除された（最後のメッセージ削除など）場合はポップオーバーを閉じる。
  useEffect(() => {
    if (openThreadId && !openCommentThread) setOpenThreadId(null);
  }, [openThreadId, openCommentThread]);

  // ポップオーバー表示中: 外側 pointerdown で閉じる。
  // ピン・ポップオーバーは内部で stopPropagation するため、それらの内部クリックでは閉じない。
  // さらに、削除確認などのモーダルダイアログ（AdHocRenderer が別 portal へ描画する）クリックでも
  // 閉じないよう、対象がコメント UI / ダイアログ内なら無視する（#7 対策）。
  // pointerdown リスナーは次マクロタスクで登録し、ポップオーバーを開いた当のクリックの pointerdown が
  // そのまま document へ伝播して即クローズされる事故を避ける。
  useEffect(() => {
    if (!openThreadId) return undefined;
    const onDocPointerDown = (event: PointerEvent): void => {
      if (isWithinCommentSurfaceOrDialog(event.target)) return;
      // 返信の送信確定処理中は閉じない（送信を中断させず、昇格済み画像の孤児化を防ぐ）。
      if (threadSubmittingRef.current) return;
      setOpenThreadId(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // 自前で消費し、グローバルの resetMode（選択 / ツール / draft の全クリア）まで
      // 巻き込まないようにする（#8 対策）。
      event.stopPropagation();
      if (threadSubmittingRef.current) return;
      setOpenThreadId(null);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("pointerdown", onDocPointerDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openThreadId]);

  // Ctrl/Cmd + ホイールによるズームを、コメント UI（ピン・ポップオーバー・入力ウィンドウ）の上でも
  // キャンバスへ効かせる。キャンバスの非passiveホイールリスナー（useWheelPanZoom）は canvas-container
  // 配下にしか張られておらず、コメント層は別の fixed オーバーレイ（兄弟要素）のため、その上での
  // Ctrl+ホイールはリスナーに届かずブラウザのページ拡大縮小が発火してしまう。
  // リスナーは window 直下ではなく、コメントの 2 つのオーバーレイ層（ピン層・サーフェス層）の
  // ルート要素へ非passive で張る。wheel はバブリングするため、各層の子（ピン・ポップオーバー・
  // 入力ウィンドウ）からのホイールはここで捕捉できる。window 直下に非passive リスナーを張ると、
  // コメントと無関係なページ全体のスクロール応答性まで低下し得る（ブラウザはハンドラが preventDefault
  // するか事前に判別できないため）。対象をオーバーレイ層へ限定して、その性能影響を局所化する。
  // Ctrl/Cmd のときだけ preventDefault してズーム（useWheelPanZoom と同一ロジック）へ振り向け、
  // 通常ホイール（スレッド一覧のスクロール等）は消費しない。
  // なお、別 portal（document.body）へ描画する操作メニュー（…）はこの 2 層の外側のため対象外。
  // メニューは小さな編集/削除用で、要件（吹き出し / ピン / 入力ウィンドウ）にも含まれないため許容する。
  useEffect(() => {
    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey && !event.metaKey) return;
      // このリスナーはコメントの 2 オーバーレイ層（ピン層 / サーフェス層）にのみ張られており、
      // 各層は pointer-events:none、子（ピン・ポップオーバー・入力ウィンドウ）だけが pointer-events:auto。
      // そのためここへバブリングしてくる wheel は必ずその子由来であり、対象要素を属性（data-comment-ui 等）
      // で絞り込む必要はない（属性文字列への暗黙依存を持たない）。Ctrl/Cmd ホイールをそのまま
      // キャンバスズームへ振り向ける。
      event.preventDefault();
      const state = useCanvasStore.getState();
      // canvas-container の外側（コメント UI 上）でのズームも「利用者が view を動かした操作」として
      // view 同期（表示位置の保存 / URL 反映）を arm する（canvas-stage の arming は canvas-container 内の
      // イベントのみが対象のため、ここで明示的に要求する）。view 更新より「先に」 arm を要求することで、
      // ストア通知が分割実行される場合でも、view 変更で走る保存 / URL 同期 effect の時点で必ず
      // armed 済みとなり、1 段ズーム直後の反映が次の view 変更まで遅れる事象を防ぐ。
      state.requestViewSyncArm();
      // キャンバス本体と同じ振り分け（マウスホイール=段階ズーム / トラックパッドのピンチ=無段階ズーム）。
      setView(applyWheelZoom(state.view, { x: event.clientX, y: event.clientY }, event));
    };
    const layers = [pinLayerRef.current, surfaceLayerRef.current].filter(
      (el): el is HTMLDivElement => el !== null
    );
    for (const el of layers) el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      for (const el of layers) el.removeEventListener("wheel", onWheel);
    };
    // surfaceShown が変わるたび（サーフェス層の mount/unmount）に張り直す。
  }, [setView, surfaceShown]);

  // 入力ウィンドウ表示中: 外側 pointerdown で取消（入力ウィンドウは内部で stopPropagation する）。
  // 配置クリック（use-pointer-mode のネイティブ pointerdown）は stopPropagation しないため、
  // リスナーを同期登録すると入力ウィンドウを開いた当の pointerdown が document まで伝播して
  // 即取消されてしまう。次マクロタスクで登録してこれを避ける。
  useEffect(() => {
    if (!commentDraft) return undefined;
    const onDocPointerDown = (event: PointerEvent): void => {
      if (isWithinCommentSurfaceOrDialog(event.target)) return;
      // 新規コメントの送信確定処理中は取消しない（送信を中断させず、昇格済み画像の孤児化を防ぐ）。
      if (draftSubmittingRef.current) return;
      setCommentDraft(null);
    };
    // 送信確定処理中の Escape を抑止する。入力欄（textarea）にフォーカスがある間は composer 側で
    // Escape を消費するが、送信ボタン押下でフォーカスがボタンへ移った後の Escape は composer を
    // 経由せず、グローバルショートカット（use-global-shortcuts の window keydown → resetMode）へ届いて
    // commentDraft を消してしまう。送信中はここで捕捉して伝播を止め、送信中断・孤児化を防ぐ。
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (!draftSubmittingRef.current) return;
      event.stopPropagation();
      event.preventDefault();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("pointerdown", onDocPointerDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [commentDraft, setCommentDraft]);

  return (
    <>
      {/* ピン層: コメントピンは常に図形（キャンバスオブジェクト）より上に表示する。
          図形・スタンプ等は transform を持つ canvas-stage（z-index:auto の独立スタッキング
          コンテキスト）の内側に描画されるため、この fixed オーバーレイ（z-index:9）は object の
          zOrder に依らず常にそれら全ての前面に出る（= コメントは図形より上が保証される）。
          一方で、ツールバー / メニュー（z-index:10）よりは背面に置く。ピン本体は
          pointer-events:auto のため、chrome より前面にするとピンが重なった領域の chrome を
          クリックできなくなる（AI レビュー指摘 1）。chrome を塞がないよう、ピンは chrome の背面に
          留める。コメント自身のポップオーバー / 入力ウィンドウ（z-index:30）と削除確認などの
          モーダル（z-index:1000）は引き続きピンより前面に出す。 */}
      <div
        ref={pinLayerRef}
        data-testid="comment-layer"
        style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9 }}
      >
        {threads.map((thread) => {
          // 図形の移動に追従中は、確定前アンカー（ステージ座標）から射影して追従表示する。
          const screen = stageToScreen(
            anchorPreviews[thread.id] ?? { x: thread.x, y: thread.y },
            view
          );
          return (
            <CommentPin
              key={thread.id}
              thread={thread}
              screenX={screen.x}
              screenY={screen.y}
              currentUserId={currentUserId}
              canAdmin={canAdmin}
              readOnly={readOnly}
              zoom={view.zoom}
              isOpen={openThreadId === thread.id}
              // 返信の送信確定処理中は、別ピンのクリックで開いているスレッドを切り替えない
              // （切り替えると送信中の返信 composer がアンマウントされ、昇格済み画像が孤児化する）。
              onOpen={() => {
                if (threadSubmitting) return;
                setOpenThreadId(thread.id);
              }}
              // 右クリックでスレッド削除（権限判定はピン側 + サーバ）。汎用 deleteObject を使う。
              // 送信中スレッドの削除は、返信 no-op と昇格済み画像の孤児化を招くため抑止する。
              onDelete={() => {
                if (threadSubmitting && openThreadId === thread.id) return;
                dispatcher.deleteObject(thread.id);
              }}
              // ドラッグ中のオフセットを受け取り、開いているポップオーバーを同じだけ追従させる。
              onDragOffsetChange={(offset) =>
                setPinDragOffset(offset ? { id: thread.id, dx: offset.dx, dy: offset.dy } : null)
              }
              onMove={(x, y) => dispatcher.transformObject(thread.id, { x, y })}
            />
          );
        })}
      </div>

      {/* 操作中サーフェス層: ポップオーバー・入力ウィンドウは常にクリックできるよう chrome より前面に置く。 */}
      {openCommentThread || commentDraft ? (
        <div
          ref={surfaceLayerRef}
          style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 30 }}
        >
          {openCommentThread
            ? (() => {
                const screen = stageToScreen(
                  anchorPreviews[openCommentThread.id] ?? {
                    x: openCommentThread.x,
                    y: openCommentThread.y,
                  },
                  view
                );
                // 開いているスレッドのピンをドラッグ中なら、ポップオーバーも同じ screen オフセットで追従させる
                // （確定後は store 更新で新しい x/y へ収束し、オフセットは null へ戻る）。
                const dragForOpen =
                  pinDragOffset && pinDragOffset.id === openThreadId ? pinDragOffset : null;
                const popoverX = screen.x + (dragForOpen ? dragForOpen.dx : 0);
                const popoverY = screen.y + (dragForOpen ? dragForOpen.dy : 0);
                return (
                  <CommentThreadPopover
                    thread={openCommentThread}
                    screenX={popoverX}
                    screenY={popoverY}
                    currentUserId={currentUserId}
                    canAdmin={canAdmin}
                    readOnly={readOnly}
                    dispatcher={dispatcher}
                    onClose={() => setOpenThreadId(null)}
                    imageUploader={readOnly ? undefined : commentImageUploader}
                    onSubmittingChange={setThreadSubmitting}
                  />
                );
              })()
            : null}

          {commentDraft && !readOnly
            ? (() => {
                const screen = stageToScreen({ x: commentDraft.x, y: commentDraft.y }, view);
                return (
                  <CommentInputWindow
                    screenX={screen.x}
                    screenY={screen.y}
                    authorName={currentUserName}
                    authorAvatar={currentUserAvatar}
                    onSubmit={(body, images) => {
                      dispatcher.createCommentThread({
                        x: commentDraft.x,
                        y: commentDraft.y,
                        body,
                        images,
                      });
                      setCommentDraft(null);
                    }}
                    onCancel={() => setCommentDraft(null)}
                    imageUploader={commentImageUploader}
                    onSubmittingChange={setDraftSubmitting}
                  />
                );
              })()
            : null}
        </div>
      ) : null}
    </>
  );
}

// クリック先がコメント UI（ピン / ポップオーバー / 入力）またはモーダルダイアログ
// （削除確認など、AdHocRenderer が別 portal へ描画する role="dialog" / "alertdialog"）内なら、
// 「外側クリック」として扱わない（ポップオーバー / 入力ウィンドウを誤って閉じない）。
function isWithinCommentSurfaceOrDialog(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('[data-comment-ui], [role="dialog"], [role="alertdialog"]') !== null
  );
}
